import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Building2, Paintbrush, Columns3, Sparkles, TriangleAlert,
    UtensilsCrossed, ShieldAlert, HeartHandshake, ArrowRight,
    GraduationCap, Warehouse,
} from "lucide-react";
import { SERVICES } from "@/data/content";
import { Reveal } from "@/lib/motion";
import { scrollToId } from "@/components/SmoothScroll";

const ICONS = { Building2, Paintbrush, Columns3, Sparkles, TriangleAlert, UtensilsCrossed, ShieldAlert, HeartHandshake, GraduationCap, Warehouse };

export default function Services() {
    return (
        <section id="services" className="py-24 md:py-32 bg-mist relative" data-testid="services-section">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="text-center mb-16 max-w-2xl mx-auto">
                    <Reveal><span className="uppercase text-xs tracking-[0.2em] font-bold text-brand-red font-body">{SERVICES.kicker}</span></Reveal>
                    <Reveal delay={0.05}><h2 className="text-3xl md:text-5xl font-medium tracking-tight text-navy mt-4 mb-5 font-display">{SERVICES.title}</h2></Reveal>
                    <Reveal delay={0.1}><div className="h-px w-16 bg-brand-red mx-auto mb-6" /></Reveal>
                    <Reveal delay={0.12}><p className="text-base text-slate-500 font-body">{SERVICES.intro}</p></Reveal>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {SERVICES.cards.map((c, i) => {
                        const Icon = ICONS[c.icon];
                        return (
                            <Reveal
                                key={c.title}
                                delay={(i % 3) * 0.08}
                                y={0}
                                className="group bg-white border border-line hover:border-electric/40 transition-all duration-500 hover:shadow-[0_20px_60px_-30px_rgba(46,123,255,0.5)] flex flex-col"
                            >
                                {c.img ? (
                                    <div className="h-48 overflow-hidden relative">
                                        <div className="french-stripe h-[3px] w-full absolute top-0 left-0 z-10" />
                                        <img src={c.img} alt={c.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-navy/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                    </div>
                                ) : (
                                    <div className="h-48 bg-navy relative overflow-hidden flex items-center justify-center">
                                        <div className="absolute inset-0" style={{ background: "radial-gradient(80% 80% at 30% 20%, rgba(46,123,255,0.35), transparent 60%)" }} />
                                        <Icon className="w-16 h-16 text-white/20" />
                                    </div>
                                )}
                                <div className="p-8 flex flex-col flex-1">
                                    <div className="w-12 h-12 flex items-center justify-center bg-navy text-white mb-5 -mt-14 relative z-10 group-hover:bg-electric transition-colors duration-300">
                                        <Icon className="w-6 h-6" />
                                    </div>
                                    <h3 className="text-xl font-medium text-navy mb-3 font-display">{c.title}</h3>
                                    <p className="text-sm leading-relaxed text-slate-500 font-body flex-1">{c.desc}</p>
                                    {c.to && (
                                        <Link
                                            to={c.to}
                                            className="group/link inline-flex items-center gap-2 mt-5 text-xs uppercase tracking-wide font-semibold text-navy border border-navy px-5 py-2.5 hover:bg-navy hover:text-white transition-colors duration-300 font-body w-fit"
                                            data-testid={`service-link-${c.to.replace("/", "")}`}
                                        >
                                            {c.cta}
                                            <ArrowRight className="w-3.5 h-3.5 group-hover/link:translate-x-1 transition-transform" />
                                        </Link>
                                    )}
                                    {c.devis && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                window.dispatchEvent(new CustomEvent("ign:prefill-service", { detail: c.devis }));
                                                scrollToId("contact");
                                            }}
                                            className="group/link inline-flex items-center gap-2 mt-5 text-xs uppercase tracking-wide font-semibold text-navy border border-navy px-5 py-2.5 hover:bg-navy hover:text-white transition-colors duration-300 font-body w-fit"
                                            data-testid={`service-devis-${c.devis.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                                        >
                                            {c.cta}
                                            <ArrowRight className="w-3.5 h-3.5 group-hover/link:translate-x-1 transition-transform" />
                                        </button>
                                    )}
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
